import React, {useRef, useMemo} from 'react';
import {Props} from './ac-icon-button.proptypes';
import clsx from 'clsx';

// Imports => Constants
import {ICONS, THEMES, VARIANTS} from '@asdf-constants';

// Imports => Utilities
import {AcUUID, AcIsSet, AcIsEmptyString} from '@asdf-utils';

// Imports => Atoms
import {AcIcon, AcRipple} from '@asdf-atoms';

const _CLASSES = {
  MAIN: 'ac-icon-button',
  ALT: 'ac-icon-button--alt',
  VARIANT: {
    TRANSPARENT: 'ac-icon-button--ghost', // backward compatibility
    TEXT: 'ac-icon-button--ghost', // backward compatibility
    GHOST: 'ac-icon-button--ghost',
    FILL: 'ac-icon-button--fill',
    FLAT: 'ac-icon-button--flat',
  },
  THEME: {
    PITCH: 'ac-icon-button--pitch',
    WHITE: 'ac-icon-button--white',
    EGGWHITE: 'ac-icon-button--eggwhite',
    LIGHT: 'ac-icon-button--light',
    MEDIUM: 'ac-icon-button--medium',
    ALPHA: 'ac-icon-button--alpha',
    ALPHA_LIGHT: 'ac-icon-button--alpha-light',
    ALPHA_MEDIUM: 'ac-icon-button--alpha-medium',
    ALPHA_DARK: 'ac-icon-button--alpha-dark',
    OMEGA: 'ac-icon-button--omega',
    OMEGA_LIGHT: 'ac-icon-button--omega-light',
    OMEGA_MEDIUM: 'ac-icon-button--omega-medium',
    OMEGA_DARK: 'ac-icon-button--omega-dark',
    OMEGA_PITCH: 'ac-icon-button--omega-pitch',
    BETA: 'ac-icon-button--beta',
    BETA_MEDIUM: 'ac-icon-button--beta-medium',
    BETA_DARK: 'ac-icon-button--beta-dark',
  },
  DISABLED: 'ac-icon-button--disabled',
};

// Component
const AcIconButton = ({
  icon = ICONS.CLOSE,
  id = AcUUID(),
  tag = 'button',
  type = 'button',
  theme = 'default',
  title = null,
  variant = 'fill',
  flat = false,
  disabled = false,
  callback,
  className,
  ...rest
}) => {
  const $element = useRef();

  const handleClick = event => {
    if (!disabled && callback) callback(event, $element.current);
  };

  const getStyleClassNames = useMemo(() => {
    return clsx(
      _CLASSES.MAIN,
      theme && _CLASSES.THEME[theme.toUpperCase()],
      variant && _CLASSES.VARIANT[variant.toUpperCase()],
      flat && _CLASSES.VARIANT.FLAT,
      disabled && _CLASSES.DISABLED,
      className
    );
  }, [disabled, theme, variant, flat, className]);

  const Tag = tag;

  const renderRipple = useMemo(() => {
    let ripple_theme;

    if ([THEMES.WHITE, THEMES.LIGHT, THEMES.SUBTLE].indexOf(theme) > -1)
      ripple_theme = THEMES.PITCH;
    else if (
      [THEMES.OMEGA, THEMES.OMEGA_LIGHT].indexOf(theme) > -1 &&
      [VARIANTS.TRANSPARENT, VARIANTS.TEXT, VARIANTS.GHOST, VARIANTS.OUTLINE].indexOf(variant) > -1
    )
      ripple_theme = THEMES.OMEGA;
    else if (
      [THEMES.ALPHA, THEMES.ALPHA_LIGHT].indexOf(theme) > -1 &&
      [VARIANTS.TRANSPARENT, VARIANTS.TEXT, VARIANTS.GHOST, VARIANTS.OUTLINE].indexOf(variant) > -1
    )
      ripple_theme = THEMES.ALPHA;
    else if (
      [THEMES.ALPHA, THEMES.ALPHA_LIGHT, THEMES.OMEGA, THEMES.OMEGA_LIGHT].indexOf(theme) > -1
    )
      ripple_theme = THEMES.PITCH;
    else if (
      [VARIANTS.TRANSPARENT, VARIANTS.TEXT, VARIANTS.GHOST, VARIANTS.OUTLINE].indexOf(variant) > -1
    )
      ripple_theme = THEMES.PITCH;

    return <AcRipple theme={ripple_theme} size={'small'} simple />;
  }, [theme, variant]);

  const renderIcon = useMemo(() => {
    let props = {};

    if (AcIsSet(title) && !AcIsEmptyString(title)) {
      props.title = title;
    }

    return <AcIcon icon={icon} {...props} />;
  }, [title]);

  return (
    <Tag
      ref={$element}
      id={id}
      type={type}
      aria-roledescription={'button'}
      disabled={disabled}
      onClick={handleClick}
      className={getStyleClassNames}
      {...rest}
    >
      {renderIcon}
      {renderRipple}
    </Tag>
  );
};

AcIconButton.displayName = 'AcIconButton';
AcIconButton.propTypes = Props;

export {AcIconButton};
export default AcIconButton;
